import React, { ForwardedRef, forwardRef, ReactElement } from 'react';
import {
  generatePath,
  Link as RouterLink,
  LinkProps as ReactLinkProps,
} from 'react-router-dom';
import { arrayUtils, commonUtils } from '@sato/components';

import {
  ExtractRouteParams,
  QUERY_STRINGS,
  QueryParamType,
  RouteParams,
} from '@routes/routes.types';

export const isInternal = (dest: string | undefined) => {
  if (!dest) return false;

  if (dest.charAt(0) === '#') return true;

  // If it's a relative url such as '/path', 'path' and does not contain a protocol we can assume it is internal.
  if (dest.indexOf(':') !== -1 || dest.indexOf('//') !== -1) {
    const parser = document.createElement('a');
    parser.href = dest;

    return window.location.hostname === parser.hostname;
  }

  return true;
};

type ToProp = ReactLinkProps['to'];

interface LinkBaseProps extends Omit<ReactLinkProps, 'to' | 'href'> {
  href?: string;
  query?: Partial<{ [key in QUERY_STRINGS]: QueryParamType<key> }>;
  openInNewTab?: boolean;
}

type LinkParameterizedProps<T extends ToProp> = T extends string
  ? {
      to: T;
    } & (ExtractRouteParams<T> extends never
      ? object
      : { params: RouteParams<T> })
  : { to?: T };

export type LinkProps<T extends ToProp = ToProp> = LinkBaseProps &
  LinkParameterizedProps<T>;

export interface Link<T extends ToProp> {
  (props: LinkProps<T>): ReactElement;
}

export const Link = forwardRef(
  <T extends ToProp>(
    { openInNewTab = false, target: targetProp, ...props }: LinkProps<T>,
    ref: ForwardedRef<HTMLAnchorElement>
  ) => {
    let to = 'to' in props ? props.to : props.href;
    const target = openInNewTab ? '_blank' : targetProp;

    if ('params' in props && typeof to === 'string') {
      // TODO: clean up once routeParams and route are typed separately
      to = generatePath(to as string, props.params as object);
    }

    if (props.query && typeof to === 'string') {
      // get current search params as base
      const searchParams = new URLSearchParams(window.location.search);

      Object.entries(props.query).forEach(([key, value]) => {
        if (commonUtils.nonNullable(value)) searchParams.set(key, value);
      });

      to = arrayUtils.joinArrayString([to, searchParams.toString()], '?');
    }

    if (
      !!to &&
      (typeof to !== 'string' ||
        // use native anchor element for opening links in new tabs and windows
        (isInternal(to) && target !== '_blank'))
    ) {
      return <RouterLink {...props} to={to} target={target} ref={ref} />;
    }

    return (
      <a
        {...props}
        href={to}
        // open external links in new tab by default
        target={target ?? '_blank'}
        ref={ref}
      />
    );
  }
) as <T extends ToProp>(
  props: LinkProps<T> & { ref?: ForwardedRef<HTMLAnchorElement> }
) => JSX.Element;

export const ExternalLink = forwardRef(
  <T extends ToProp>(
    props: LinkProps<T>,
    ref: ForwardedRef<HTMLAnchorElement>
  ) => <Link {...props} ref={ref} openInNewTab />
) as <T extends ToProp>(
  props: LinkProps<T> & { ref?: ForwardedRef<HTMLAnchorElement> }
) => JSX.Element;
